import Cross from "@icons/cross.svg?react";
import Search from "@icons/search.svg?url";
import debounce from "lodash-es/debounce";
import React from "react";
import { useSearchParams } from "react-router-dom";

const useSearchFieldParams = (debounceMs = 300) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchInUrl = searchParams.get("search") ?? "";
  const [localValue, setLocalValue] = React.useState(searchInUrl);

  React.useEffect(() => {
    setLocalValue(searchInUrl);
  }, [searchInUrl]);

  const updateSearchParam = React.useCallback(
    debounce((search: string) => {
      setSearchParams((c) => {
        if (search.length > 0) {
          c.set("search", search);
        } else {
          c.delete("search");
        }
        return c;
      });
    }, debounceMs),
    [setSearchParams, debounceMs],
  );

  const setValue = React.useCallback(
    (search: string) => {
      setLocalValue(search);
      updateSearchParam(search);
    },
    [updateSearchParam],
  );

  const clearValue = React.useCallback(() => {
    setLocalValue("");
    updateSearchParam.cancel();
    setSearchParams((c) => {
      c.delete("search");
      return c;
    });
  }, [setSearchParams, updateSearchParam]);

  return [localValue, setValue, clearValue] as const;
};

const SearchField = ({ placeholder }: { placeholder: string }) => {
  const [value, setValue, clearValue] = useSearchFieldParams();
  return (
    <div className="relative">
      <input
        className={`group-has-[[role=alert]]:border-can-russet mt-2 block w-full rounded-lg border p-4 font-bold ${value.length > 0 ? "bg-none!" : ""}`}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        style={{
          background: `url(${Search}) right 16px center no-repeat`,
        }}
        type="text"
        value={value}
      />
      {value.length > 0 ? (
        <button className="absolute top-[25px] right-4" onClick={clearValue}>
          <Cross />
        </button>
      ) : null}
    </div>
  );
};

export { SearchField, useSearchFieldParams };
